export type FeedbackStatus =
  | "payment_pending"
  | "pending"
  | "assigned"
  | "in_progress"
  | "completed"
  | "cancelled"
  | string;

const statusMap: Record<
  string,
  {
    key: string;
    label: string;
    className: string;
    description: string;
  }
> = {
  payment_pending: {
    key: "payment_pending",
    label: "결제 대기",
    className: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    description: "결제가 완료되지 않았습니다.",
  },
  pending: {
    key: "pending",
    label: "접수 완료",
    className: "bg-violet-500/15 text-violet-300 border border-violet-500/30",
    description: "결제가 완료되어 프로 배정을 기다리는 중입니다.",
  },
  assigned: {
    key: "assigned",
    label: "프로 배정",
    className: "bg-blue-500/15 text-blue-300 border border-blue-500/30",
    description: "전문가가 배정되었습니다. 피드백 작성이 곧 시작됩니다.",
  },
  in_progress: {
    key: "in_progress",
    label: "피드백 작성 중",
    className: "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30",
    description: "전문가가 피드백을 작성하고 있습니다.",
  },
  completed: {
    key: "completed",
    label: "피드백 완료",
    className: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
    description: "피드백이 완료되었습니다. 리포트를 확인하세요.",
  },
  cancelled: {
    key: "cancelled",
    label: "취소됨",
    className: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
    description: "신청이 취소되었습니다.",
  },
};

const legacyKoreanToKey: Record<string, keyof typeof statusMap> = {
  대기중: "pending",
  배정됨: "assigned",
  작성중: "in_progress",
  완료: "completed",
};

export function getFeedbackStatusMeta(status: FeedbackStatus) {
  const raw = String(status || "pending");
  const normalized =
    statusMap[raw]?.key ||
    legacyKoreanToKey[raw] ||
    (statusMap[raw] ? raw : "pending");

  return statusMap[normalized] ?? statusMap.pending;
}

export function getFeedbackStatusLabel(status: FeedbackStatus) {
  return getFeedbackStatusMeta(status).label;
}

export function getFeedbackStatusClass(status: FeedbackStatus) {
  return getFeedbackStatusMeta(status).className;
}

export function getFeedbackStatusDescription(status: FeedbackStatus) {
  return getFeedbackStatusMeta(status).description;
}

export function isFeedbackRequestInProgress(status: FeedbackStatus) {
  return ["payment_pending", "pending", "assigned", "in_progress"].includes(
    String(status)
  );
}

/** 프로 배정 전(접수·결제대기)만 취소 가능. 배정 이후는 불가. */
export function canCancelFeedbackRequest(
  status: FeedbackStatus,
  paymentStatus?: string,
  assignedProId?: string | null
) {
  const s = String(status);
  if (assignedProId) return false;
  if (
    s === "cancelled" ||
    s === "completed" ||
    s === "in_progress" ||
    s === "assigned"
  ) {
    return false;
  }
  if (s === "payment_pending") return true;
  if (s === "pending") {
    return paymentStatus !== "refunded";
  }
  return false;
}

/** 프로 배정 전 + 결제 완료 건만 환불 */
export function canRefundFeedbackRequest(
  status: FeedbackStatus,
  paymentStatus?: string,
  assignedProId?: string | null
) {
  if (assignedProId) return false;
  return status === "pending" && paymentStatus === "paid";
}
